import './style.scss';
import LaunchCampaignCost from 'Components/campaigns/applications/LaunchCampaignCost';
import { PRIVILEGE } from 'constants/privilege';
import React, { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { Spinner } from 'reactstrap';
import { ShortlistedSummary } from 'types/campaign/applications';
import { formatNumber } from 'utils/helpers';
import AccessControl from 'wrappers/AccessControl';
import { usePrivileges } from 'hooks/useAuth';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { campaignIconSVG } from 'pages/Campaigns/Campaign';

const TIER_ORDER = ['nano', 'micro', 'macro', 'mega'];

const creatorWord = (count: number) => (count === 1 ? 'creator' : 'creators');

const formatTierLabel = (tierKey: string, count: number): string => {
    const normalized = tierKey.replace(/_/g, ' ').toLowerCase().trim();
    const word = creatorWord(count);

    if (/\b(creator|creators)\b/.test(normalized)) {
        return normalized.replace(/\bcreators?\b/g, word);
    }

    return `${normalized} ${word}`;
};

const formatCurrency = (amount: number): string =>
    `₹${Math.round(amount).toLocaleString('en-IN')}`;

const formatRoiMetric = (value: number): string => {
    if (!value) return '0';
    const formatted = formatNumber(value);
    return formatted === '-' ? '0' : formatted;
};

const sortTierEntries = (creatorTiers: ShortlistedSummary['creator_tiers']) =>
    Object.entries(creatorTiers).sort(([a], [b]) => {
        const aIndex = TIER_ORDER.indexOf(a.toLowerCase());
        const bIndex = TIER_ORDER.indexOf(b.toLowerCase());
        const aRank = aIndex === -1 ? TIER_ORDER.length : aIndex;
        const bRank = bIndex === -1 ? TIER_ORDER.length : bIndex;
        if (aRank !== bRank) return aRank - bRank;
        return a.localeCompare(b);
    });

export interface ShortlistedSummaryPanelProps {
    summary: ShortlistedSummary | null;
    loading?: boolean;
    hasTransactions?: boolean;
    onLaunchCampaign?: () => void;
    launchingCampaign?: boolean;
}

const ShortlistedSummaryPanel: React.FC<ShortlistedSummaryPanelProps> = ({
    summary,
    loading = false,
    hasTransactions = false,
    onLaunchCampaign,
    launchingCampaign = false,
}) => {
    const [costModalOpen, setCostModalOpen] = useState(false);
    const tierEntries = summary ? sortTierEntries(summary.creator_tiers) : [];
    const totalInfluencers = summary?.application_count ?? 0;
    const totalCost = summary?.cost?.net ?? 0;
    const canLaunch = totalInfluencers > 0 && !loading;
    const privileges = usePrivileges();

    const launchWithPrivilegeCheck = () => {
        if (!canLaunch || !summary || launchingCampaign) return;
        if (!privileges.includes(PRIVILEGE.CAMPAIGN.APPLICATION.PROCESS)) {
            toast.error("You don't have permission to launch campaign!");
            return;
        }
        onLaunchCampaign?.();
    };

    const handleLaunchClick = async () => {
        if (!canLaunch || !summary || launchingCampaign) return;
        if (hasTransactions) {
            setCostModalOpen(true);
        } else {
            const r = await Swal.fire({
                title: 'Are you sure you want to start collaboration?',
                iconHtml: campaignIconSVG,
                showCancelButton: true,
                confirmButtonText: "Yes, Proceed",
                cancelButtonText: "No, Cancel",
                reverseButtons: true
            })
            if(!r.isConfirmed) return;
            launchWithPrivilegeCheck();
        }
    };

    useEffect(() => {
        if (launchingCampaign) {
            setCostModalOpen(false);
        }
    }, [launchingCampaign]);

    if (loading) {
        return (
            <aside className="shortlisted-summary" aria-label="Shortlisted summary">
                <div className="shortlisted-summary__card shortlisted-summary__card--loading">
                    <Skeleton height={160} className="w-100 d-block rounded mb-3" />
                    <Skeleton height={48} className="w-100 d-block rounded mb-3" />
                    <Skeleton height={36} className="w-100 d-block rounded" />
                </div>
            </aside>
        );
    }

    return (
        <aside className="shortlisted-summary" aria-label="Shortlisted summary">
            <div className="shortlisted-summary__card">
                <div className="shortlisted-summary__content">
                    <div className="shortlisted-summary__breakdown-card">
                        {tierEntries.length > 0 ? (
                            <div className="shortlisted-summary__tier-columns">
                                <div className="shortlisted-summary__tier-labels">
                                    {tierEntries.map(([tierKey, tier]) => (
                                        <p key={tierKey} className="shortlisted-summary__tier-label">
                                            <span className="shortlisted-summary__tier-count">
                                                {String(tier.count).padStart(2, '0')}
                                            </span>
                                            <span>{` - ${formatTierLabel(tierKey, tier.count)}`}</span>
                                        </p>
                                    ))}
                                </div>
                                <div className="shortlisted-summary__tier-costs">
                                    {tierEntries.map(([tierKey, tier]) => (
                                        <p key={tierKey} className="shortlisted-summary__tier-cost">
                                            {formatCurrency(tier.cost)}
                                        </p>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <p className="shortlisted-summary__empty-tiers text-muted mb-0">
                                No shortlisted creators yet
                            </p>
                        )}
                        {tierEntries.length > 0 && <div className="shortlisted-summary__divider" />}
                        <div className="shortlisted-summary__total-row">
                            <p className="shortlisted-summary__total-label">
                                Total {creatorWord(totalInfluencers)} ({totalInfluencers})
                            </p>
                            <p className="shortlisted-summary__total-cost">{formatCurrency(totalCost)}</p>
                        </div>
                    </div>
                    <div className="shortlisted-summary__roi">
                        <p className="shortlisted-summary__roi-title">EXPECTED ROI</p>
                        <div className="shortlisted-summary__roi-grid">
                            <p className="shortlisted-summary__roi-metric">
                                <span>Views: </span>
                                <span className="shortlisted-summary__roi-value">
                                    {formatRoiMetric(summary?.roi?.average_views ?? 0)}
                                </span>
                            </p>
                            <p className="shortlisted-summary__roi-metric">
                                <span>Engagement: </span>
                                <span className="shortlisted-summary__roi-value">
                                    {formatRoiMetric(summary?.roi?.average_engagement ?? 0)}
                                </span>
                            </p>
                            <p className="shortlisted-summary__roi-metric">
                                <span>CPV: </span>
                                <span className="shortlisted-summary__roi-value">
                                    {formatRoiMetric(summary?.roi?.cpv ?? 0)}
                                </span>
                            </p>
                            <p className="shortlisted-summary__roi-metric">
                                <span>CPE: </span>
                                <span className="shortlisted-summary__roi-value">
                                    {formatRoiMetric(summary?.roi?.cpe ?? 0)}
                                </span>
                            </p>
                        </div>
                    </div>
                </div>
                <AccessControl privilege={PRIVILEGE.CAMPAIGN.APPLICATION.PROCESS}>
                    <button
                        type="button"
                        className="shortlisted-summary__launch-btn"
                        disabled={!canLaunch || launchingCampaign}
                        onClick={handleLaunchClick}
                        aria-busy={launchingCampaign}
                    >
                        {launchingCampaign ? (
                            <>
                                <Spinner size="sm" color="light" className="shortlisted-summary__launch-spinner" />
                                PLEASE WAIT...
                            </>
                        ) : (
                            'START COLLABORATION'
                        )}
                    </button>
                    <p className="shortlisted-summary__launch-btn-description">
                        This will notify the creators that they are approved for this campaign.
                    </p>
                </AccessControl>
            </div>
            {summary && hasTransactions ? (
                <LaunchCampaignCost
                    summary={summary}
                    isOpen={costModalOpen}
                    launchingCampaign={launchingCampaign}
                    onClose={() => setCostModalOpen(false)}
                    onLaunch={launchWithPrivilegeCheck}
                />
            ) : null}
        </aside>
    );
};

export default ShortlistedSummaryPanel;
